import { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../lib/supabase';
import { userWriteHeaders, userReadHeaders } from '../lib/api';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '');

const DEFAULT_AMOUNTS = [
  { pence: 100,  label: '£1'  },
  { pence: 200,  label: '£2'  },
  { pence: 500,  label: '£5'  },
  { pence: 1000, label: '£10' },
  { pence: 2000, label: '£20' },
  { pence: 5000, label: '£50' },
];

function calcFee(pence) { return Math.ceil(pence * 0.015) + 20; }

function fmt(pence) {
  return `£${(pence / 100).toFixed(2)}`;
}

function TopUpForm({ userId, currentBalance, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();

  const [selectedPence, setSelectedPence] = useState(1000);
  const [customMode, setCustomMode]       = useState(false);
  const [customVal, setCustomVal]         = useState('');
  const [status, setStatus]               = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg]           = useState('');
  const pollRef = useRef(null);

  // Poll wallet balance after payment until it increases (webhook has landed)
  useEffect(() => {
    if (status !== 'success') return;
    let attempts = 0;
    const MAX = 10; // give up after 20 seconds

    async function poll() {
      attempts++;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) { onSuccess(); return; }
        const r = await fetch('/api/wallet', { headers: userReadHeaders(userId, token) });
        if (r.ok) {
          const data = await r.json();
          if (data.balancePence > currentBalance || attempts >= MAX) {
            onSuccess(data.balancePence);
            return;
          }
        }
      } catch { /* non-fatal */ }
      pollRef.current = setTimeout(poll, 2000);
    }

    pollRef.current = setTimeout(poll, 2000);
    return () => clearTimeout(pollRef.current);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsedCustom = Math.round(parseFloat(customVal) * 100) || 0;
  const activePence  = customMode ? parsedCustom : selectedPence;
  const feePence     = activePence >= 100 ? calcFee(activePence) : 0;
  const credited     = activePence - feePence;
  const canSubmit    = activePence >= 100 && activePence <= 20000 && credited > 0;

  function selectPreset(pence) {
    setSelectedPence(pence);
    setCustomMode(false);
    setCustomVal('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements || !canSubmit) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in');

      const res = await fetch('/api/wallet', {
        method: 'POST',
        headers: userWriteHeaders(userId, token),
        body: JSON.stringify({ amountPence: activePence }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Top-up failed');

      const { error } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) throw new Error(error.message);

      setStatus('success');
      setTimeout(onSuccess, 2000);
    } catch (err) {
      setErrorMsg(err.message);
      setStatus('error');
    }
  }

  return (
    <form className="topup-form" onSubmit={handleSubmit}>
      <div className="topup-form__balance">
        Current balance: <strong>{fmt(currentBalance)}</strong>
      </div>

      <div className="topup-form__amounts">
        {DEFAULT_AMOUNTS.map(a => (
          <button
            key={a.pence}
            type="button"
            className={`topup-form__amount-btn${!customMode && selectedPence === a.pence ? ' active' : ''}`}
            onClick={() => selectPreset(a.pence)}
          >
            {a.label}
          </button>
        ))}
        <button
          type="button"
          className={`topup-form__amount-btn${customMode ? ' active' : ''}`}
          onClick={() => { setCustomMode(true); setCustomVal(''); }}
        >
          Custom
        </button>
      </div>

      {customMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.75rem', color: 'var(--text2)' }}>£</span>
          <input
            type="number"
            min="1" max="200" step="0.01"
            placeholder="e.g. 7.50"
            value={customVal}
            onChange={e => setCustomVal(e.target.value)}
            autoFocus
            style={{
              flex: 1, padding: '0.5rem 0.7rem',
              background: 'var(--bg3)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--text)', fontSize: '0.9rem',
              outline: 'none',
            }}
          />
          {activePence > 0 && activePence < 100 && (
            <span style={{ fontSize: '0.65rem', color: 'var(--text3)' }}>Min £1</span>
          )}
        </div>
      )}

      {/* Fee breakdown */}
      {canSubmit && (
        <div style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '0.58rem', letterSpacing: '0.06em',
          color: 'var(--text3)', marginBottom: '0.85rem',
          display: 'flex', flexDirection: 'column', gap: '0.2rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>You pay</span>
            <span>{fmt(activePence)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(239,100,80,0.7)' }}>
            <span>Stripe processing fee</span>
            <span>− {fmt(feePence)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--em)', fontWeight: 600, borderTop: '1px solid var(--border)', paddingTop: '0.2rem', marginTop: '0.1rem' }}>
            <span>Credited to wallet</span>
            <span>{fmt(credited)}</span>
          </div>
          <div style={{ marginTop: '0.3rem', opacity: 0.6, lineHeight: 1.4 }}>
            Stripe fee deducted transparently. Al-Diwan adds no markup.
          </div>
        </div>
      )}

      <div className="topup-form__card">
        <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
      </div>

      {errorMsg && <p className="topup-form__error">{errorMsg}</p>}

      {status === 'success' ? (
        <p className="topup-form__success">Payment successful! Updating balance…</p>
      ) : (
        <button
          type="submit"
          className="topup-form__submit"
          disabled={status === 'loading' || !stripe || !elements || !canSubmit}
        >
          {!stripe ? 'Loading…' : status === 'loading' ? 'Processing…' : canSubmit ? `Pay ${fmt(activePence)} → credit ${fmt(credited)}` : 'Select an amount'}
        </button>
      )}
    </form>
  );
}

export default function TopUpSheet({ userId, currentBalance, onSuccess, onClose }) {
  return (
    <div className="bottom-sheet-overlay" onClick={onClose}>
      <div className="bottom-sheet" onClick={e => e.stopPropagation()}>
        <div className="bottom-sheet__header">
          <h2 className="bottom-sheet__title">Top up wallet</h2>
          <button className="bottom-sheet__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <Elements stripe={stripePromise}>
          <TopUpForm
            userId={userId}
            currentBalance={currentBalance}
            onSuccess={onSuccess}
          />
        </Elements>

        <p className="topup-form__note">
          Payments processed securely by Stripe. Balance credited immediately on confirmation.
        </p>
      </div>
    </div>
  );
}
